<template>
  <div class="page-shell">
    <CCard class="border-0 shadow-sm page-card">
      <CCardHeader class="page-header d-flex flex-wrap justify-content-between align-items-center">
        <div class="mr-3 mb-2 mb-md-0">
          <div class="text-uppercase small eyebrow">Finance</div>
          <strong class="h4 d-block mb-1">Installments</strong>
          <div class="small text-muted">Track pending installments and add new payments</div>
        </div>

        <div class="d-flex flex-wrap align-items-end tools-wrap">
          <CInput class="mr-md-2 mb-2 mb-md-0" label="Order Number" v-model.trim="searchOrderNumber" />
          <CButton color="primary" class="mr-2 mb-2 mb-md-0" :disabled="isSearching" @click="searchInstallment">
            {{ isSearching ? 'Searching...' : 'Search' }}
          </CButton>
          <CButton color="secondary" variant="outline" class="mb-2 mb-md-0" :disabled="isLoadingList" @click="loadPending">
            {{ isLoadingList ? 'Refreshing...' : 'Refresh List' }}
          </CButton>
        </div>
      </CCardHeader>

      <CCardBody>
        <CAlert v-if="errorMessage" color="danger" show class="mb-3">{{ errorMessage }}</CAlert>
        <CAlert v-if="successMessage" color="success" show class="mb-3">{{ successMessage }}</CAlert>

        <CRow>
          <CCol lg="7" class="mb-3 mb-lg-0">
            <CCard class="panel-card border-0 h-100">
              <CCardHeader class="panel-header">Pending Installments</CCardHeader>
              <CCardBody>
                <div v-if="isLoadingList" class="text-center py-4 text-muted">Loading installments...</div>

                <div v-else-if="!pendingRows.length" class="empty-state text-center py-4">
                  <div class="h5 mb-2">No items</div>
                  <div class="text-muted">No pending installments found.</div>
                </div>

                <div v-else class="table-responsive table-wrap">
                  <table class="table table-sm table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Order Number</th>
                        <th>Contact Name</th>
                        <th>Contact Phone</th>
                        <th>Next Installment</th>
                        <th>Remaining Amount</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in pendingRows" :key="row.order_number">
                        <td>{{ row.order_number }}</td>
                        <td>{{ row.contact_name }}</td>
                        <td>{{ row.contact_phone }}</td>
                        <td>{{ row.next_installment || '-' }}</td>
                        <td>{{ formatCurrency(row.remaining_amount) }}</td>
                        <td>
                          <CButton color="primary" size="sm" variant="outline" @click="viewOrder(row.order_number)">
                            View
                          </CButton>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CCardBody>
            </CCard>
          </CCol>

          <CCol lg="5">
            <CCard class="panel-card border-0 h-100">
              <CCardHeader class="panel-header">Installment Details</CCardHeader>
              <CCardBody>
                <div v-if="!selectedOrder" class="text-muted py-2">Search or click View from the list to load installment details.</div>

                <div v-else>
                  <div class="detail-grid mb-3">
                    <div>
                      <div class="detail-label">Order Number</div>
                      <div class="detail-value">{{ selectedOrder.order_number }}</div>
                    </div>
                    <div>
                      <div class="detail-label">Contact</div>
                      <div class="detail-value">{{ selectedOrder.contact_name }}</div>
                    </div>
                    <div>
                      <div class="detail-label">Phone</div>
                      <div class="detail-value">{{ selectedOrder.contact_phone }}</div>
                    </div>
                    <div>
                      <div class="detail-label">Status</div>
                      <div class="detail-value text-capitalize">{{ selectedOrder.status }}</div>
                    </div>
                    <div>
                      <div class="detail-label">Total Amount</div>
                      <div class="detail-value">{{ formatCurrency(selectedOrder.total_amount) }}</div>
                    </div>
                    <div>
                      <div class="detail-label">Remaining</div>
                      <div class="detail-value">{{ formatCurrency(selectedOrder.remaining_amount) }}</div>
                    </div>
                  </div>

                  <div class="d-flex align-items-end flex-wrap add-wrap mb-3">
                    <CInput
                      class="mr-md-2 mb-2 mb-md-0"
                      label="Add Installment Amount"
                      type="number"
                      min="0"
                      v-model.number="newAmount"
                    />
                    <CButton color="success" :disabled="isSaving" class="mb-2 mb-md-0" @click="addInstallment">
                      {{ isSaving ? 'Saving...' : 'Add' }}
                    </CButton>
                  </div>

                  <div class="table-responsive table-wrap">
                    <table class="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Amount</th>
                          <th>User Id</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!selectedOrder.installment_records || !selectedOrder.installment_records.length">
                          <td colspan="4" class="text-center text-muted">No records</td>
                        </tr>
                        <tr v-for="(record, index) in selectedOrder.installment_records" :key="record.id || index">
                          <td>{{ index + 1 }}</td>
                          <td>{{ formatCurrency(record.amount) }}</td>
                          <td>{{ record.user_id }}</td>
                          <td>{{ formatDate(record.created_at) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'Installments',
  data () {
    return {
      pendingRows: [],
      selectedOrder: null,
      searchOrderNumber: '',
      newAmount: null,
      isLoadingList: false,
      isSearching: false,
      isSaving: false,
      errorMessage: '',
      successMessage: ''
    }
  },
  created () {
    this.loadPending()
  },
  methods: {
    authToken () {
      return localStorage.getItem('api_token')
    },
    parseError (error, fallback) {
      const validationErrors = error?.response?.data?.error
      if (validationErrors && typeof validationErrors === 'object') {
        const key = Object.keys(validationErrors)[0]
        if (key && Array.isArray(validationErrors[key]) && validationErrors[key][0]) {
          return validationErrors[key][0]
        }
      }
      return error?.response?.data?.message || fallback
    },
    formatCurrency (value) {
      const num = Number(value || 0)
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(num)
    },
    formatDate (value) {
      if (!value) return '-'
      const date = new Date(Number(value) * 1000)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleString()
    },
    loadPending () {
      this.errorMessage = ''
      this.successMessage = ''
      this.isLoadingList = true

      axios.get(`${this.$apiAdress}/api/installments?token=${this.authToken()}`)
        .then((response) => {
          this.pendingRows = Array.isArray(response.data) ? response.data : []
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to load installments list.')
        })
        .finally(() => {
          this.isLoadingList = false
        })
    },
    viewOrder (orderNumber) {
      this.searchOrderNumber = orderNumber
      this.searchInstallment()
    },
    searchInstallment () {
      this.errorMessage = ''
      this.successMessage = ''

      if (!this.searchOrderNumber) {
        this.errorMessage = 'Order Number is required.'
        return
      }

      this.isSearching = true
      axios.post(
        `${this.$apiAdress}/api/installments/search?token=${this.authToken()}`,
        { order_number: this.searchOrderNumber }
      )
        .then((response) => {
          this.selectedOrder = response.data || null
        })
        .catch((error) => {
          console.log(error)
          this.selectedOrder = null
          this.errorMessage = this.parseError(error, 'Unable to search installment details.')
        })
        .finally(() => {
          this.isSearching = false
        })
    },
    addInstallment () {
      this.errorMessage = ''
      this.successMessage = ''

      if (!this.selectedOrder?.order_number) {
        this.errorMessage = 'Load an order first.'
        return
      }
      if (!this.newAmount || Number(this.newAmount) <= 0) {
        this.errorMessage = 'Amount must be greater than zero.'
        return
      }

      this.isSaving = true
      axios.post(
        `${this.$apiAdress}/api/installments?token=${this.authToken()}`,
        {
          order_number: this.selectedOrder.order_number,
          amount: Number(this.newAmount)
        }
      )
        .then((response) => {
          this.successMessage = response?.data?.message || 'Installment added successfully.'
          this.newAmount = null
          this.searchInstallment()
          this.loadPending()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to add installment.')
        })
        .finally(() => {
          this.isSaving = false
        })
    }
  }
}
</script>

<style scoped>
.page-shell {
  background: linear-gradient(180deg, #f5f8fd 0%, #eef3fa 100%);
}

.page-card {
  border-radius: 16px;
}

.page-header {
  background: linear-gradient(135deg, #ffffff 0%, #f6f9ff 100%);
}

.eyebrow {
  letter-spacing: 0.08em;
  color: #66748a;
  font-weight: 700;
}

.tools-wrap {
  gap: 0.5rem;
}

.panel-card {
  border-radius: 14px;
  background: #fff;
}

.panel-header {
  background: #f8fbff;
  color: #4a5872;
  font-weight: 700;
}

.table-wrap {
  border: 1px solid #e7edf5;
  border-radius: 12px;
  overflow: hidden;
}

.table-wrap thead th {
  background: #f8fbff;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.74rem;
  white-space: nowrap;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.detail-label {
  color: #6b778d;
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.detail-value {
  color: #18233a;
  font-weight: 600;
}

.add-wrap {
  gap: 0.5rem;
}

.empty-state {
  border: 1px dashed #d7dfeb;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.72);
}

@media (max-width: 768px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
